import { Directive, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { OnDestroyBase } from '../../helpers/public-helpers';
import { HeaderLoginService } from '../../services/public-services';

interface LoggedInContext {
    $implicit: boolean;
    loggedIn: boolean;
    employee: boolean;
}

/**
 * Conveniently write the user's login state to a template variable.
 * Currently supports "loggedIn" and "employee" (whether or not the user is an employee).
 *
 * To get the login state, use either the implicit or explicit declaration:
 * ```html
 * <!-- implicit -->
 * <p *oscLoggedIn="let userIsLoggedIn"></p>
 * <!-- explicit -->
 * <p *oscLoggedIn="let userIsLoggedIn = loggedIn"></p>
 * ```
 *
 * To get the user's employee state, use the explicit variant:
 * ```html
 * <p *oscLoggedIn="let userIsEmployee = employee"></p>
 * ```
 *
 * Obviously you can name the variables whatever you like.
 */
@Directive({ selector: '[oscLoggedIn]' })
export class LoggedInDirective extends OnDestroyBase implements OnDestroy {
    private context: LoggedInContext = {} as LoggedInContext;

    public constructor(
        private vcRef: ViewContainerRef,
        private templateRef: TemplateRef<LoggedInContext>,
        private loginService: HeaderLoginService
    ) {
        super();

        const view = this.vcRef.createEmbeddedView<LoggedInContext>(this.templateRef, this.context);

        this.loginService.loggedIn
            .pipe(takeUntil(this.destroySubject))
            .subscribe((loggedIn) => (view.context.loggedIn = view.context.$implicit = loggedIn));

        this.loginService.employee
            .pipe(takeUntil(this.destroySubject))
            .subscribe((employee) => (view.context.employee = employee));
    }
}
